/**
 * Sanitizes a query string to prevent SQL injection attacks
 * - Removes SQL operators and dangerous characters
 * - Allows spaces, alphanumeric (including Spanish/international chars), and common safe punctuation by default
 * - Trims leading/trailing whitespace
 *
 * @param query - The query string to sanitize
 * @param allowedChars - Optional: additional characters to allow (e.g., ['@', '#'])
 * @returns Sanitized query string or empty string if invalid
 *
 * @example
 * sanitizeQuery("John Doe")           // "John Doe"
 * sanitizeQuery("José García")        // "José García"
 * sanitizeQuery("Peña")               // "Peña"
 * sanitizeQuery("Trade-123")          // "Trade-123"
 * sanitizeQuery("' OR 1=1--")         // "OR 11--" (SQL operators removed)
 * sanitizeQuery("test@email.com", ['@']) // "test@email.com"
 */
export const ALLOWED_QUERY_CHARS = 'a-zA-Z0-9\\s._,\\-\\u00C0-\\u00FF\\u0100-\\u017F'
export const ALLOWED_QUERY_REGEXP = new RegExp(`^[${ALLOWED_QUERY_CHARS}]*$`)
export const ALLOWED_QUERY_REGEXP_STR = `^[${ALLOWED_QUERY_CHARS}]*$`

export const sanitizeQuery = (query: string, allowedChars?: string[]): string => {
  if (!query || typeof query !== 'string') {
    return ''
  }

  // Caracteres peligrosos para SQL injection que siempre se bloquean:
  // ' " ; = < > ( ) \ % | & { } [ ]
  const dangerousChars = /['"`;=<>()\\%|&{}\[\]]/g

  // Primero eliminar caracteres peligrosos
  let sanitized = query.replace(dangerousChars, '')

  // Permitir por defecto:
  // - Letras básicas (a-z, A-Z)
  // - Números (0-9)
  // - Espacios (\s)
  // - Puntuación segura: . _ , -
  // - Caracteres latinos extendidos (\u00C0-\u00FF): incluye ñ, á, é, í, ó, ú, ç, ü, etc.
  // - Caracteres latinos adicionales (\u0100-\u017F): para idiomas del este de Europa
  // Esto cubre español, francés, alemán, italiano, portugués, y otros idiomas europeos
  let allowedPattern = ALLOWED_QUERY_CHARS

  if (allowedChars && allowedChars.length > 0) {
    // Agregar caracteres adicionales permitidos
    const escaped = allowedChars.map(char => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('')
    allowedPattern += escaped
  }

  // Eliminar cualquier carácter no permitido
  const pattern = new RegExp(`[^${allowedPattern}]`, 'g')
  sanitized = sanitized.replace(pattern, '')

  // Normalizar espacios múltiples a uno solo y hacer trim
  sanitized = sanitized.replace(/\s+/g, ' ').trim()

  return sanitized
}

/**
 * Validates if a sanitized query is valid for API calls
 * @param sanitizedQuery - The already sanitized query
 * @param minLength - Minimum length required (default: 1)
 * @returns boolean indicating if query is valid
 */
export const isValidQuery = (sanitizedQuery: string, minLength: number = 1): boolean => {
  return sanitizedQuery.length >= minLength
}

/**
 * Detects potential SQL injection patterns in a query
 * Useful for logging/monitoring suspicious activity
 * @param query - Original query before sanitization
 * @returns boolean indicating if query contains suspicious patterns
 */
export const hasSuspiciousPatterns = (query: string): boolean => {
  if (!query) return false

  const sqlKeywords =
    /\b(UNION|SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|SCRIPT)\b/i
  const sqlOperators = /('.*OR.*'|".*OR.*"|--|\*\/|\/\*|;.*DROP|;.*DELETE)/i

  return sqlKeywords.test(query) || sqlOperators.test(query)
}
